import { Component } from '@theme/component';
import { debounce, fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { chileRegions } from './cl-regions.js';

/**
 * A custom element that handles RUT, region, and commune collection for Chilean customers.
 */
class CartPrecheckout extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {((event: Event) => void) | null} */
  #cartUpdateHandler = null;

  /** @type {((event: Event) => void) | null} */
  #dialogOpenHandler = null;

  /** @type {((event: Event) => void) | null} */
  #inputHandler = null;

  /** @type {((event: Event) => Promise<void>) | null} */
  #blurHandler = null;

  /** @type {((event: Event) => void) | null} */
  #regionChangeHandler = null;

  /** @type {((event: Event) => void) | null} */
  #communeChangeHandler = null;

  /** @type {MutationObserver | null} */
  #regionSelectObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#setupDrawerOpenListener();
    this.#setupRestoreListeners();
    // Defer DOM-dependent setup: Horizon may reset cart HTML after connectedCallback
    setTimeout(() => {
      this.#setupRutValidation();
      this.#populateRegions();
      this.#watchForSelectReset();
      this.#setupRegionCommuneHandlers();
      this.#restoreRutValue();
      this.#restoreRegionCommune();
    }, 0);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#reinitializeComponent();
  }

  #setupDrawerOpenListener() {
    if (this.#dialogOpenHandler) {
      document.removeEventListener('dialog:open', this.#dialogOpenHandler);
    }

    this.#dialogOpenHandler = (event) => {
      const drawer = event.target;
      if (drawer instanceof HTMLElement && drawer.classList.contains('cart-drawer')) {
        setTimeout(() => {
          this.#reinitializeComponent();
        }, 100);
      }
    };

    document.addEventListener('dialog:open', this.#dialogOpenHandler);
  }

  #reinitializeComponent() {
    requestAnimationFrame(() => {
      const rutInput = this.querySelector('#billing_rut');
      if (!(rutInput instanceof HTMLInputElement)) {
        setTimeout(() => {
          const retryInput = this.querySelector('#billing_rut');
          if (retryInput instanceof HTMLInputElement) {
            this.#setupRutValidation();
            this.#populateRegions();
            this.#setupRegionCommuneHandlers();
            this.#restoreRutValue();
            this.#restoreRegionCommune();
          }
        }, 100);
        return;
      }

      this.#setupRutValidation();
      this.#populateRegions();
      this.#setupRegionCommuneHandlers();
      this.#restoreRutValue();
      this.#restoreRegionCommune();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#cartUpdateHandler) {
      document.removeEventListener('cart:updated', this.#cartUpdateHandler);
      this.#cartUpdateHandler = null;
    }

    if (this.#dialogOpenHandler) {
      document.removeEventListener('dialog:open', this.#dialogOpenHandler);
      this.#dialogOpenHandler = null;
    }

    const rutInput = this.querySelector('#billing_rut');
    if (rutInput instanceof HTMLInputElement) {
      if (this.#inputHandler) {
        rutInput.removeEventListener('input', this.#inputHandler);
        this.#inputHandler = null;
      }
      if (this.#blurHandler) {
        rutInput.removeEventListener('blur', this.#blurHandler);
        this.#blurHandler = null;
      }
    }

    const regionSelect = this.querySelector('#billing_region');
    if (regionSelect instanceof HTMLSelectElement && this.#regionChangeHandler) {
      regionSelect.removeEventListener('change', this.#regionChangeHandler);
      this.#regionChangeHandler = null;
    }

    const communeSelect = this.querySelector('#billing_commune');
    if (communeSelect instanceof HTMLSelectElement && this.#communeChangeHandler) {
      communeSelect.removeEventListener('change', this.#communeChangeHandler);
      this.#communeChangeHandler = null;
    }

    this.#regionSelectObserver?.disconnect();
    this.#regionSelectObserver = null;

    this.#activeFetch?.abort();
    this.#activeFetch = null;
  }

  /**
   * Handles updates to cart attributes.
   * @param {Event} event - The input event from form fields.
   */
  updateCartAttributes = (event) => {
    if (!event?.target) return;

    if (
      event.target instanceof HTMLInputElement &&
      event.target.id === 'billing_rut'
    ) {
      this.#validateRut(event.target);
      this.#updateCartAttributesDebounced(event);
      return;
    }

    this.#updateCartAttributesDebounced(event);
  };

  /**
   * @param {Event} event
   */
  #updateCartAttributesDebounced = debounce(async (event) => {
    await this.#saveToCart();
    if (event) {
      cartPerformance.measureFromEvent('precheckout-update:user-action', event);
    }
  }, 500);

  #populateRegions() {
    const regionSelect = this.querySelector('#billing_region');
    if (!(regionSelect instanceof HTMLSelectElement)) return;

    const currentValue = regionSelect.value;

    // Keep only the default placeholder option
    regionSelect.innerHTML = '<option value="">Seleccione su región</option>';

    for (const region of chileRegions) {
      const opt = document.createElement('option');
      opt.value = region.name;
      opt.textContent = region.name;
      regionSelect.appendChild(opt);
    }

    // Restore selection if it was already set
    if (currentValue) regionSelect.value = currentValue;
  }

  // Watches for Horizon morphing the select back to 1 option and repopulates reactively.
  #watchForSelectReset() {
    this.#regionSelectObserver?.disconnect();

    const regionSelect = this.querySelector('#billing_region');
    if (!(regionSelect instanceof HTMLSelectElement)) return;

    this.#regionSelectObserver = new MutationObserver(() => {
      if (regionSelect.options.length <= 1) {
        this.#populateRegions();
        this.#restoreRegionCommune();
      }
    });

    this.#regionSelectObserver.observe(regionSelect, { childList: true });
  }

  #setupRegionCommuneHandlers() {
    const regionSelect = this.querySelector('#billing_region');
    const communeSelect = this.querySelector('#billing_commune');

    if (!(regionSelect instanceof HTMLSelectElement)) return;
    if (!(communeSelect instanceof HTMLSelectElement)) return;

    if (this.#regionChangeHandler) {
      regionSelect.removeEventListener('change', this.#regionChangeHandler);
    }
    if (this.#communeChangeHandler) {
      communeSelect.removeEventListener('change', this.#communeChangeHandler);
    }

    this.#regionChangeHandler = (event) => {
      this.#onRegionChange(regionSelect.value);
      this.#removeFieldError('billing_region_error');
      regionSelect.classList.remove('cart-precheckout__input--error');
      this.#updateCartAttributesDebounced(event);
    };

    this.#communeChangeHandler = (event) => {
      this.#removeFieldError('billing_commune_error');
      communeSelect.classList.remove('cart-precheckout__input--error');
      this.#updateCartAttributesDebounced(event);
    };

    regionSelect.addEventListener('change', this.#regionChangeHandler);
    communeSelect.addEventListener('change', this.#communeChangeHandler);
  }

  /** @param {string} regionName */
  #onRegionChange(regionName) {
    const communeSelect = this.querySelector('#billing_commune');
    if (!(communeSelect instanceof HTMLSelectElement)) return;

    communeSelect.innerHTML = '';

    if (!regionName) {
      communeSelect.disabled = true;
      const opt = document.createElement('option');
      opt.value = '';
      opt.textContent = 'Seleccione región primero';
      communeSelect.appendChild(opt);
      return;
    }

    const region = chileRegions.find((r) => r.name === regionName);
    if (!region) {
      communeSelect.disabled = true;
      return;
    }

    communeSelect.disabled = false;

    const defaultOpt = document.createElement('option');
    defaultOpt.value = '';
    defaultOpt.textContent = 'Seleccione su comuna';
    communeSelect.appendChild(defaultOpt);

    for (const commune of region.communes) {
      const opt = document.createElement('option');
      opt.value = commune;
      opt.textContent = commune;
      communeSelect.appendChild(opt);
    }
  }

  /** @param {string} value */
  #isBlacklistedRut(value) {
    const cleaned = this.#rutUtils.clean(value);
    if (!cleaned || cleaned.length < 2) return false;

    const numberPart = cleaned.slice(0, -1);
    const dv = cleaned.slice(-1);

    if (numberPart.length !== 7 && numberPart.length !== 8) return false;

    const first = numberPart[0];
    const allSame = numberPart.split('').every((c) => c === first);
    return allSame && dv === first;
  }

  /** @param {string} value */
  #isAllowedRut(value) {
    return this.#rutUtils.validate(value) && !this.#isBlacklistedRut(value);
  }

  async #saveToCart() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;

    try {
      const rutInput = this.querySelector('#billing_rut');
      const regionSelect = this.querySelector('#billing_region');
      const communeSelect = this.querySelector('#billing_commune');

      if (!(rutInput instanceof HTMLInputElement)) return;

      const rutFormatted = rutInput.value.trim();
      const regionValue = regionSelect instanceof HTMLSelectElement ? regionSelect.value : '';
      const communeValue = communeSelect instanceof HTMLSelectElement ? communeSelect.value : '';

      if (!rutFormatted) {
        this.#saveToLocalStorage('');
        this.#saveRegionCommuneToLocalStorage(regionValue, communeValue);
        await this.#updateCartAttributes('', regionValue, communeValue, abortController);
        return;
      }

      if (!this.#isAllowedRut(rutFormatted)) return;

      this.#saveToLocalStorage(rutFormatted);
      this.#saveRegionCommuneToLocalStorage(regionValue, communeValue);
      await this.#updateCartAttributes(rutFormatted, regionValue, communeValue, abortController);
    } catch (error) {
      this.#handleError(error, 'saveToCart');
    } finally {
      this.#activeFetch = null;
    }
  }

  /** @param {string} rutFormatted */
  #saveToLocalStorage(rutFormatted) {
    try {
      if (!rutFormatted) {
        localStorage.removeItem('cart_billing_rut');
        return;
      }

      if (!this.#isAllowedRut(rutFormatted)) return;

      localStorage.setItem('cart_billing_rut', rutFormatted);
    } catch (e) {
      this.#handleError(e, 'saveToLocalStorage');
    }
  }

  /**
   * @param {string} regionValue
   * @param {string} communeValue
   */
  #saveRegionCommuneToLocalStorage(regionValue, communeValue) {
    try {
      if (regionValue) {
        localStorage.setItem('cart_shipping_province', regionValue);
        const provinceCode = chileRegions.find((r) => r.name === regionValue)?.code;
        if (provinceCode) {
          localStorage.setItem('cart_shipping_province_code', provinceCode);
        } else {
          localStorage.removeItem('cart_shipping_province_code');
        }
      } else {
        localStorage.removeItem('cart_shipping_province');
        localStorage.removeItem('cart_shipping_province_code');
      }
      if (communeValue) {
        localStorage.setItem('cart_shipping_city', communeValue);
      } else {
        localStorage.removeItem('cart_shipping_city');
      }
    } catch (e) {
      this.#handleError(e, 'saveRegionCommuneToLocalStorage');
    }
  }

  /**
   * @param {string} rutValue
   * @param {string} regionValue
   * @param {string} communeValue
   * @param {AbortController} abortController
   */
  async #updateCartAttributes(rutValue, regionValue, communeValue, abortController) {
    const body = {
      attributes: {
        billing_rut: rutValue,
        shipping_province: regionValue,
        shipping_city: communeValue,
      },
    };

    const config = fetchConfig('json', {
      body: JSON.stringify(body),
    });

    await fetch(Theme.routes.cart_update_url, {
      ...config,
      signal: abortController.signal,
    });
  }

  /**
   * @param {unknown} error
   * @param {string} context
   */
  #handleError(error, context) {
    if (
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        window.location.search.includes('debug=true'))
    ) {
      console.error(`[CartPrecheckout] ${context}:`, error);
    }
  }

  #setupRutValidation() {
    const rutInput = this.querySelector('#billing_rut');
    if (!(rutInput instanceof HTMLInputElement)) return;

    if (this.#inputHandler) {
      rutInput.removeEventListener('input', this.#inputHandler);
    }
    if (this.#blurHandler) {
      rutInput.removeEventListener('blur', this.#blurHandler);
    }

    this.#inputHandler = (event) => {
      if (event.target instanceof HTMLInputElement) {
        this.#formatRutInput(event.target);
        this.#validateRut(event.target);
        this.#updateCartAttributesDebounced(event);
      }
    };
    rutInput.addEventListener('input', this.#inputHandler);

    this.#blurHandler = async (event) => {
      if (event.target instanceof HTMLInputElement) {
        this.#formatRutInput(event.target);
        const isValid = this.#validateRut(event.target);
        if (isValid || !event.target.value.trim()) {
          await this.#saveToCart();
        }
      }
    };
    rutInput.addEventListener('blur', this.#blurHandler);
  }

  /** @param {HTMLInputElement} input */
  #formatRutInput(input) {
    const value = input.value.trim();

    if (!value) {
      input.value = '';
      return;
    }

    const cleaned = this.#rutUtils.clean(value);

    if (!cleaned || cleaned.length < 2) {
      return;
    }

    const formatted = this.#rutUtils.format(cleaned, { dots: true });
    input.value = formatted;
  }

  /** @param {HTMLInputElement} input */
  #validateRut(input) {
    const value = input.value.trim();

    input.classList.remove('cart-precheckout__input--error');
    this.#removeRutError();

    if (!value) return true;

    const isValid = this.#rutUtils.validate(value);

    if (!isValid) {
      input.classList.add('cart-precheckout__input--error');
      this.#showRutError('RUT inválido. Verifique el formato y el dígito verificador.');
      return false;
    }

    if (this.#isBlacklistedRut(value)) {
      input.classList.add('cart-precheckout__input--error');
      this.#showRutError('RUT inválido.');
      return false;
    }

    return true;
  }

  /** @param {string} message */
  #showRutError(message) {
    const rutField = this.querySelector('#billing_rut')?.closest('.cart-precheckout__field');
    if (!rutField) return;

    this.#removeRutError();

    const errorElement = document.createElement('span');
    errorElement.className = 'cart-precheckout__error';
    errorElement.id = 'rut-error-message';
    errorElement.textContent = message;
    errorElement.setAttribute('role', 'alert');

    rutField.appendChild(errorElement);
  }

  #removeRutError() {
    this.querySelector('#rut-error-message')?.remove();
  }

  /**
   * @param {HTMLSelectElement} selectEl
   * @param {string} errorId
   * @param {string} message
   */
  #showSelectError(selectEl, errorId, message) {
    this.#removeFieldError(errorId);

    const field = selectEl.closest('.cart-precheckout__field');
    if (!field) return;

    const errorElement = document.createElement('span');
    errorElement.className = 'cart-precheckout__error';
    errorElement.id = errorId;
    errorElement.textContent = message;
    errorElement.setAttribute('role', 'alert');

    field.appendChild(errorElement);
  }

  /** @param {string} errorId */
  #removeFieldError(errorId) {
    this.querySelector(`#${errorId}`)?.remove();
  }

  validateBeforeCheckout() {
    const rutInput = this.querySelector('#billing_rut');
    const regionSelect = this.querySelector('#billing_region');
    const communeSelect = this.querySelector('#billing_commune');

    let isValid = true;
    /** @type {HTMLElement | null} */
    let firstError = null;

    // Validate RUT
    if (rutInput instanceof HTMLInputElement) {
      const rutValue = rutInput.value.trim();
      const isRequired = this.dataset.requireRut === 'true' || rutInput.required;

      if (isRequired && !rutValue) {
        rutInput.classList.add('cart-precheckout__input--error');
        this.#showRutError('El RUT es requerido');
        isValid = false;
        firstError = firstError ?? rutInput;
      } else if (rutValue && !this.#validateRut(rutInput)) {
        isValid = false;
        firstError = firstError ?? rutInput;
      }
    }

    // Validate region
    if (regionSelect instanceof HTMLSelectElement && regionSelect.required) {
      if (!regionSelect.value) {
        regionSelect.classList.add('cart-precheckout__input--error');
        this.#showSelectError(regionSelect, 'billing_region_error', 'La región es requerida');
        isValid = false;
        firstError = firstError ?? regionSelect;
      }
    }

    // Validate commune
    if (communeSelect instanceof HTMLSelectElement && communeSelect.required) {
      if (!communeSelect.value) {
        communeSelect.classList.add('cart-precheckout__input--error');
        this.#showSelectError(communeSelect, 'billing_commune_error', 'La comuna es requerida');
        isValid = false;
        firstError = firstError ?? communeSelect;
      }
    }

    if (firstError) {
      const target = firstError;
      requestAnimationFrame(() => {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        target.focus();
      });
    }

    return isValid;
  }

  async updateCartAttributesBeforeCheckout() {
    await this.#saveToCart();
  }

  #restoreRutValue() {
    requestAnimationFrame(() => {
      const rutInput = this.querySelector('#billing_rut');
      if (!(rutInput instanceof HTMLInputElement)) return;

      const rutFromCart = rutInput.value.trim();
      const rutFromStorage = this.#getRutFromStorage();

      if (rutFromStorage) {
        this.#restoreFromStorage(rutInput, rutFromStorage, rutFromCart);
        return;
      }

      if (rutFromCart) {
        this.#restoreFromCart(rutInput, rutFromCart);
      } else {
        this.#restoreFromCartApi();
      }
    });

    this.#setupRestoreListeners();
  }

  #restoreRegionCommune() {
    const regionSelect = this.querySelector('#billing_region');
    const communeSelect = this.querySelector('#billing_commune');

    if (!(regionSelect instanceof HTMLSelectElement)) return;
    if (!(communeSelect instanceof HTMLSelectElement)) return;

    try {
      const region = localStorage.getItem('cart_shipping_province') || '';
      const commune = localStorage.getItem('cart_shipping_city') || '';

      if (region) {
        regionSelect.value = region;
        this.#onRegionChange(region);
        if (commune) communeSelect.value = commune;
        return;
      }
    } catch (e) {
      this.#handleError(e, 'restoreRegionCommune');
    }

    this.#restoreRegionCommuneFromCartApi();
  }

  async #restoreRegionCommuneFromCartApi() {
    try {
      const response = await fetch(Theme.routes.cart_url + '.js');
      if (!response.ok) return;

      const cart = await response.json();
      const region = cart.attributes?.shipping_province || '';
      const commune = cart.attributes?.shipping_city || '';

      if (!region) return;

      const regionSelect = this.querySelector('#billing_region');
      if (!(regionSelect instanceof HTMLSelectElement)) return;

      regionSelect.value = region;
      this.#onRegionChange(region);

      if (commune) {
        const communeSelect = this.querySelector('#billing_commune');
        if (communeSelect instanceof HTMLSelectElement) {
          communeSelect.value = commune;
        }
      }

      this.#saveRegionCommuneToLocalStorage(region, commune);
    } catch (error) {
      this.#handleError(error, 'restoreRegionCommuneFromCartApi');
    }
  }

  #getRutFromStorage() {
    try {
      const value = localStorage.getItem('cart_billing_rut');
      const rut = value?.trim() || null;

      if (rut && !this.#isAllowedRut(rut)) {
        localStorage.removeItem('cart_billing_rut');
        return null;
      }

      return rut;
    } catch (e) {
      this.#handleError(e, 'getRutFromStorage');
      return null;
    }
  }

  /**
   * @param {HTMLInputElement} input
   * @param {string} storageValue
   * @param {string} cartValue
   */
  #restoreFromStorage(input, storageValue, cartValue) {
    input.value = storageValue;
    this.#formatRutInput(input);
    this.#validateRut(input);

    if (!cartValue) {
      this.#saveToCart();
    }
  }

  /**
   * @param {HTMLInputElement} input
   * @param {string} cartValue
   */
  #restoreFromCart(input, cartValue) {
    if (!this.#isAllowedRut(cartValue)) return;

    this.#formatRutInput(input);
    this.#saveToLocalStorage(cartValue);
    this.#validateRut(input);
  }

  #setupRestoreListeners() {
    if (this.#cartUpdateHandler) {
      document.removeEventListener('cart:updated', this.#cartUpdateHandler);
    }

    this.#cartUpdateHandler = () => {
      setTimeout(() => {
        this.#setupRutValidation();
        this.#populateRegions();
        this.#setupRegionCommuneHandlers();

        const updatedRutInput = this.querySelector('#billing_rut');
        if (!(updatedRutInput instanceof HTMLInputElement)) return;

        const currentValue = updatedRutInput.value.trim();
        if (!currentValue) {
          const rutFromStorage = this.#getRutFromStorage();
          if (rutFromStorage) {
            this.#restoreFromStorage(updatedRutInput, rutFromStorage, currentValue);
            this.#saveToCart();
          } else {
            this.#restoreFromCartApi();
          }
        } else {
          this.#formatRutInput(updatedRutInput);
          this.#saveToLocalStorage(currentValue);
          this.#validateRut(updatedRutInput);
        }

        this.#restoreRegionCommune();
      }, 100);
    };

    document.addEventListener('cart:updated', this.#cartUpdateHandler);
  }

  async #restoreFromCartApi() {
    try {
      const response = await fetch(Theme.routes.cart_url + '.js');
      if (!response.ok) return;

      const cart = await response.json();
      const rutFromCart = cart.attributes?.billing_rut;

      if (!rutFromCart) return;
      if (!this.#isAllowedRut(rutFromCart)) return;

      const rutInput = this.querySelector('#billing_rut');
      if (!(rutInput instanceof HTMLInputElement)) return;

      rutInput.value = rutFromCart;
      this.#formatRutInput(rutInput);
      this.#saveToLocalStorage(rutFromCart);
      this.#validateRut(rutInput);
    } catch (error) {
      this.#handleError(error, 'restoreFromCartApi');
    }
  }

  #rutUtils = {
    /** @param {string} rut */
    clean: function (rut) {
      return typeof rut === 'string'
        ? rut.replace(/^0+|[^0-9kK]+/g, '').toUpperCase()
        : '';
    },

    /** @param {string} rut */
    validate: function (rut) {
      if (typeof rut !== 'string') return false;

      rut = this.clean(rut);
      if (!rut || rut.length < 2) return false;

      const numberPart = rut.slice(0, -1);
      const dv = rut.slice(-1);

      if (numberPart.length < 7) return false;
      if (!/^\d+$/.test(numberPart)) return false;
      if (!/^[0-9K]$/.test(dv)) return false;

      let t = parseInt(numberPart, 10);
      let m = 0;
      let s = 1;

      while (t > 0) {
        s = (s + (t % 10) * (9 - (m++ % 6))) % 11;
        t = Math.floor(t / 10);
      }

      const expected = s > 0 ? String(s - 1) : 'K';
      return expected === dv;
    },

    /**
     * @param {string} rut
     * @param {{ dots: boolean }} [options]
     */
    format: function (rut, options = { dots: true }) {
      if (!rut) {
        return '';
      }

      rut = this.clean(rut);
      if (!rut || rut.length < 2) {
        return rut;
      }

      let result;
      if (options.dots) {
        const numberPart = rut.slice(0, -1);
        const checkDigit = rut.slice(-1);

        const groups = [];
        for (let i = numberPart.length; i > 0; i -= 3) {
          const start = Math.max(0, i - 3);
          groups.unshift(numberPart.slice(start, i));
        }

        result = groups.join('.') + '-' + checkDigit;
      } else {
        result = rut.slice(0, -1) + '-' + rut.slice(-1);
      }

      return result;
    },

    /** @param {string} input */
    getCheckDigit: function (input) {
      const rut = Array.from(this.clean(input), Number);

      if (rut.length === 0 || rut.includes(NaN)) {
        return '';
      }

      const modulus = 11;
      const initialValue = 0;
      const sumResult = rut
        .reverse()
        .reduce(
          (accumulator, currentValue, index) =>
            accumulator + currentValue * ((index % 6) + 2),
          initialValue
        );

      const checkDigit = modulus - (sumResult % modulus);

      if (checkDigit === 10) {
        return 'K';
      } else if (checkDigit === 11) {
        return '0';
      } else {
        return checkDigit.toString();
      }
    },
  };
}

if (!customElements.get('cart-precheckout')) {
  customElements.define('cart-precheckout', CartPrecheckout);
}
